import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { ShieldCheck, Eye, EyeOff } from "lucide-react";

function PasswordField({ value, onChange, testid, minLength, label }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="block mb-2 text-sm font-semibold text-ink">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"} required minLength={minLength}
          value={value} onChange={onChange}
          className="w-full rounded-xl border border-[var(--border-default)] bg-white px-4 pr-12 h-14 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none"
          data-testid={testid}
        />
        <button type="button" onClick={() => setShow((v) => !v)} data-testid={`${testid}-toggle`}
          aria-label={show ? "Masquer" : "Afficher"}
          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg text-ink-muted hover:text-brand hover:bg-subtle">
          {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}

export default function ChangePassword() {
  const { t } = useI18n();
  const { refreshMe } = useAuth();
  const nav = useNavigate();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      await refreshMe();
      toast.success(t("common.success"));
      nav("/app");
    } catch (err) {
      toast.error(apiError(err, t("common.error")));
    } finally {
      setLoading(false);
    }
  };

  const input = "w-full rounded-xl border border-[var(--border-default)] bg-white px-4 h-14 text-base focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none";
  // `input` kept for non-password fields if any

  return (
    <div className="min-h-screen shell-gradient flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white rounded-2xl border border-[var(--border-default)] p-8 shadow-sm">
        <div className="w-14 h-14 rounded-2xl bg-brand/10 text-brand flex items-center justify-center mb-4">
          <ShieldCheck className="w-7 h-7" />
        </div>
        <h1 className="font-heading text-2xl font-extrabold text-ink">{t("auth.mustChangeTitle")}</h1>
        <p className="mt-2 text-ink-muted">{t("auth.mustChangeSubtitle")}</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <PasswordField label={t("auth.currentPassword")} value={cur} onChange={(e) => setCur(e.target.value)} testid="cp-current" />
          <PasswordField label={t("auth.newPassword")} value={nw} onChange={(e) => setNw(e.target.value)} minLength={6} testid="cp-new" />
          <button
            type="submit" disabled={loading} data-testid="cp-submit"
            className="w-full h-14 rounded-xl bg-brand hover:bg-brand-hover text-white font-heading font-bold transition-colors disabled:opacity-60 active:scale-[0.98]"
          >
            {loading ? t("common.loading") : t("common.save")}
          </button>
        </form>
      </div>
    </div>
  );
}
